import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
} from "react";
import { Snackbar, Alert, AlertColor, Button } from "@mui/material";

type SnackbarOptions = {
  message: string;
  severity?: AlertColor;
  autoHideDuration?: number;
  /** A button beside the message, e.g. Undo. Pressing it closes the message. */
  action?: { label: string; onClick: () => void };
};

interface SnackbarContextType {
  showMessage: (options: SnackbarOptions) => void;
  /** Closes the message showing now, e.g. before a dialog it would cover. */
  hideMessage: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

interface SnackbarProviderProps {
  children: ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<AlertColor>("info");
  const [autoHideDuration, setAutoHideDuration] = useState<number | undefined>(
    4000
  );
  const [action, setAction] = useState<SnackbarOptions["action"]>();

  const showMessage = useCallback(
    (options: SnackbarOptions) => {
      setMessage(options.message);
      setSeverity(options.severity || "info");
      setAutoHideDuration(options.autoHideDuration ?? 4000);
      setAction(() => options.action);
      setOpen(true);
    },
    []
  );

  const hideMessage = useCallback(() => setOpen(false), []);

  const handleClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  return (
    <SnackbarContext.Provider value={{ showMessage, hideMessage }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleClose}
          action={
            action ? (
              <Button
                size="small"
                onClick={() => {
                  setOpen(false);
                  action.onClick();
                }}
                sx={{ fontWeight: 600 }}
              >
                {action.label}
              </Button>
            ) : undefined
          }
          severity={severity}
          variant="outlined"
          sx={(theme) => ({
            width: "100%",
            maxWidth: 560,
            bgcolor: "background.paper",
            color: "text.primary",
            borderColor: "divider",
            borderLeft: "4px solid",
            borderLeftColor: severity === "success" ? "primary.main" : `${severity}.main`,
            borderRadius: 2,
            boxShadow: theme.shadows[3],
            alignItems: "center",
            px: 2,
            py: 1,
            typography: "body2",
            fontWeight: 500,
            "& .MuiAlert-icon": {
              color: severity === "success" ? "primary.main" : `${severity}.main`,
              opacity: 1,
            },
            "& .MuiAlert-message": { py: 0.5, overflowWrap: "anywhere" },
            "& .MuiAlert-action": { color: "text.secondary", pt: 0, alignItems: "center" },
          })}
        >
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

/** The Snackbar when there is one: for code that may also run outside a SnackbarProvider. */
export const useOptionalSnackbar = (): SnackbarContextType | undefined => useContext(SnackbarContext);

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};

